import React, { useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import './Modal.css';

/**
 * Reusable Modal Component
 * 
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Close handler
 * @param {string} title - Modal title
 * @param {ReactNode} children - Modal content
 * @param {ReactNode} footer - Footer content (buttons, etc.)
 * @param {string} size - Modal size: 'small', 'medium', 'large', 'fullscreen'
 * @param {string} className - Additional CSS classes for the modal content box
 * @param {string} overlayClassName - Additional CSS classes for the overlay (e.g. for z-index when nested)
 * @param {boolean} closeOnOverlayClick - Close when clicking overlay (default: false - user must use buttons)
 * @param {boolean} closeOnEscape - Close when pressing Escape (default: false - user must use buttons)
 * @param {boolean} showCloseButton - Show close button (default: true)
 */
export const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'large',
  className = '',
  overlayClassName = '',
  closeOnOverlayClick = false,
  closeOnEscape = false,
  showCloseButton = true,
  ...props
}) => {
  // Prevent body and document scroll when modal is open (removes background scrollbar)
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle ESC key (only when closeOnEscape is true)
  useEffect(() => {
    const handleEsc = (event) => {
      if (closeOnEscape && event.keyCode === 27 && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const overlayClasses = ['modal-overlay', overlayClassName].filter(Boolean).join(' ');

  return (
    <div
      className={overlayClasses}
      onClick={handleOverlayClick}
      {...props}
    >
      <div
        className={`modal-content modal-${size} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showCloseButton) && (
          <div className="modal-header">
            {title && <h3 className="modal-title">{title}</h3>}
            {showCloseButton && (
              <button
                className="modal-close"
                onClick={onClose}
                aria-label="Close modal"
              >
                <FiX size={24} />
              </button>
            )}
          </div>
        )}
        
        <div className="modal-body">
          {children}
        </div>
        
        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Confirmation Modal Component
 */
export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger', // 'danger', 'warning', 'info'
  ...props
}) => {
  const footer = (
    <>
      <button
        className={`btn btn-secondary`}
        onClick={onClose}
      >
        {cancelText}
      </button>
      <button
        className={`btn btn-${variant}`}
        onClick={() => {
          onConfirm();
          onClose();
        }}
      >
        {confirmText}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="small"
      footer={footer}
      {...props}
    >
      <p>{message}</p>
    </Modal>
  );
};
